import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { Box } from '@mui/material';

import { useContextMenuItemsWithAccess } from 'src/components/common/context_menu/context_menu_items_with_access';
import { InfoCard } from 'src/components/common/infocard/InfoCard';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { IInfoColumn } from 'src/components/common/types/infocardTypes';
import PageWithLayout from 'src/components/layout/PageWithLayout';
import { CreateEditPatientDrawer } from 'src/features/patient/components/CreateEditPatientDrawer';
import {
	deletePatient,
	getPatientDetails,
	PATIENT_LOADING_KEY,
} from 'src/features/patient/store/patientThunks';
import { StudyTable } from 'src/features/study/components/StudyTable';
import { AppDispatch, RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

const PatientDetailsPage: React.FC = () => {
	const { patientId, siteSlug } = useParams<{
		patientId: string;
		siteSlug: string;
	}>();
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();
	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const patientDetails = useSelector(
		(state: RootState) => state.patient.patientDetails,
	);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [shouldRefetchData, setShouldRefetchData] = useState(false);

	const breadcrumbs = [
		{
			label: siteDetails?.name || 'Site Details',
			href: `/site/${siteSlug}`,
		},
		{ label: patientDetails?.site_patient_id || 'Site Patient ID' },
	];

	const handleDeletePatient = async () => {
		if (!siteSlug || !patientDetails) return;
		dispatch(
			deletePatient({
				siteSlug: siteSlug,
				patientId: patientDetails.id,
				onSuccess: () => {
					snackbar('Patient deleted successfully', 'success');
					setIsDeleteModalOpen(false);
					navigate(`/site/${siteSlug}`, { replace: true });
				},
			}),
		);
	};

	useEffect(() => {
		if (patientId && siteSlug) {
			dispatch(
				getPatientDetails({
					patientId: Number(patientId),
					siteSlug: siteSlug,
				}),
			);
		}
	}, [patientId, siteSlug, shouldRefetchData]);

	const parsedContextMenuItems = useContextMenuItemsWithAccess([
		{
			label: 'Edit',
			icon: <ModeEditOutlinedIcon />,
			onClick: () => setIsEditModalOpen(true),
			allowedSiteRoles: ['super_admin', 'admin', 'data_collector'],
		},
		{
			label: 'Delete',
			icon: <DeleteOutlinedIcon color='error' />,
			onClick: () => setIsDeleteModalOpen(true),
			color: 'error',
			allowedSiteRoles: ['super_admin', 'admin', 'data_collector'],
		},
	]);

	const infoColumns: IInfoColumn[] = [
		{
			label: 'Gender',
			value: patientDetails?.gender || '-',
		},
		{
			label: 'Age',
			value: patientDetails?.age ? patientDetails?.age.toString() : '-',
		},
		{
			label: 'Address',
			value: patientDetails?.address || '-',
		},
		{
			label: 'Contact Number',
			value: patientDetails?.contact_number || '-',
		},
		{
			label: 'Occupation',
			value: patientDetails?.occupation || '-',
		},
		{
			label: 'Created By',
			value:
				`${patientDetails?.created_by.first_name} ${patientDetails?.created_by.last_name}` ||
				'-',
		},
		{
			label: 'Created At',
			value: patientDetails?.created_at
				? formatTimestamp(patientDetails.created_at)
				: '-',
		},
	];

	return (
		<PageWithLayout breadcrumbs={breadcrumbs}>
			<Box sx={{ p: 0.5 }}>
				<InfoCard
					title={
						patientDetails
							? `${patientDetails.first_name} ${patientDetails.last_name} `
							: '-'
					}
					columns={infoColumns}
					loadingKey={PATIENT_LOADING_KEY}
					contextMenuItems={parsedContextMenuItems}
				/>

				<StudyTable
					data={patientDetails?.studies || []}
					onStudyCreation={(studyDetails) =>
						navigate(
							`/site/${siteSlug}/patient/${patientId}/study/${studyDetails.id}`,
						)
					}
					onStudyDeleted={() => setShouldRefetchData((prev) => !prev)}
				/>

				{patientDetails && (
					<ConfirmationModal
						title='Are you sure you want to delete the patient?'
						modalContent={
							<>
								This action cannot be undone. All data related
								to the patient <br></br>
								<strong>
									{patientDetails.first_name}{' '}
									{patientDetails.last_name}
								</strong>{' '}
								will be permanently deleted.
							</>
						}
						modalOpen={isDeleteModalOpen}
						handleYes={handleDeletePatient}
						loadingKey={PATIENT_LOADING_KEY}
						handleNo={() => setIsDeleteModalOpen(false)}
					/>
				)}

				{patientDetails && (
					<CreateEditPatientDrawer
						open={isEditModalOpen}
						onClose={() => setIsEditModalOpen(false)}
						onSubmit={() => {
							setShouldRefetchData((prev) => !prev);
							setIsEditModalOpen(false);
						}}
						initialPatientData={patientDetails}
					/>
				)}
			</Box>
		</PageWithLayout>
	);
};

export default PatientDetailsPage;
